import { createAction, handleActions } from "redux-actions";
import { CUSTOM_MENU_ACTION_TYPE, CUSTOM_MENU_DOMAIN, CUSTOM_MENU_TYPE, MENU_LIST } from "@constants";
import {
    getCustomMenuSetsAPI,
    getCustomMenuSetDetailAPI,
    createCustomMenuSetAPI,
    addCustomMenuAPI,
    updateCustomMenuAPI,
    deleteCustomMenuAPI,
    updateCustomMenuActionsAPI,
} from "@modules/apis/custom-menu-sets";
import { getFoldersListAPI } from "@modules/apis/virtureFolder";
import { getCategoryGroupCategoriesAPI } from "@modules/categoryGroupsManagement";
import { getCollectionsAPI } from "@modules/apis/collections";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";

const initialState = {
    setId: null,
    hasCustomContent: false,
    menuSetSettings: null,
    data: null,
    pending: false,
    error: null,
};

const GET_CUSTOM_MENUS_PENDING = "GET_CUSTOM_MENUS_PENDING";
const GET_CUSTOM_MENUS_SUCCESS = "GET_CUSTOM_MENUS_SUCCESS";
const GET_CUSTOM_MENUS_FAILURE = "GET_CUSTOM_MENUS_FAILURE";
const GET_CUSTOM_MENU_SET_DETAIL_PENDING = "GET_CUSTOM_MENU_SET_DETAIL_PENDING";
const GET_CUSTOM_MENU_SET_DETAIL_SUCCESS = "GET_CUSTOM_MENU_SET_DETAIL_SUCCESS";
const GET_CUSTOM_MENU_SET_DETAIL_FAILURE = "GET_CUSTOM_MENU_SET_DETAIL_FAILURE";
const UPDATE_MENU_SET_SETTINGS_STATE = "UPDATE_MENU_SET_SETTINGS_STATE";
const RESET_CUSTOM_MENUS = "RESET_CUSTOM_MENUS";

export default handleActions(
    {
        [GET_CUSTOM_MENUS_PENDING]: (state) => {
            return { ...state, data: null, pending: true, error: null };
        },
        [GET_CUSTOM_MENUS_SUCCESS]: (state, action) => {
            const { data, setId, hasCustomContent, projectId } = action.payload;
            return { ...state, data, setId, hasCustomContent, projectId, pending: false };
        },
        [GET_CUSTOM_MENUS_FAILURE]: (state, action) => {
            return { ...state, data: null, pending: false, error: action.payload };
        },
        [GET_CUSTOM_MENU_SET_DETAIL_PENDING]: (state) => {
            return { ...state, menuSetSettings: null, pending: true, error: null };
        },
        [GET_CUSTOM_MENU_SET_DETAIL_SUCCESS]: (state, action) => {
            return { ...state, menuSetSettings: action.payload, pending: false };
        },
        [GET_CUSTOM_MENU_SET_DETAIL_FAILURE]: (state, action) => {
            return { ...state, menuSetSettings: null, pending: false, error: action.payload };
        },
        [UPDATE_MENU_SET_SETTINGS_STATE]: (state, action) => {
            return { ...state, menuSetSettings: { ...state.menuSetSettings, menus: action.payload } };
        },
        [RESET_CUSTOM_MENUS]: (state, action) => {
            return { ...state, data: null };
        },
    },
    initialState,
);

export const resetCustomMenus = createAction(RESET_CUSTOM_MENUS);

export const createCustomMenuSet =
    ({ data }) =>
    (dispatch, getState) => {
        const { stage, project } = getState();

        return new Promise((resolve, reject) => {
            createCustomMenuSetAPI(stage.endpoint, project.id, data)
                .then(async (response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

const parseMenuData = (data) => {
    let parsedData = { ...data };
    delete parsedData.action;
    delete parsedData.validationErrors;

    const parseAttributes = (attributes) => {
        if (!attributes) return null;
        let _attributes = {};
        const { virtualFolder, categories, collections, customMetadata, ...rest } = attributes;
        if (virtualFolder) {
            _attributes.virtualFolder = {
                driveId: virtualFolder.driveId,
                virtualFolderId: virtualFolder.virtualFolderId,
            };
        }
        if (categories) {
            _attributes.categories = {
                categoryGroupId: categories.categoryGroupId,
                categories: categories.categories?.map((c) => ({ id: c.id })),
            };
        }
        if (collections) {
            _attributes.collections = collections.map((c) => ({ id: c.id }));
        }
        if (customMetadata) {
            _attributes.customMetadata = { id: customMetadata.id };
        }
        return { ..._attributes, ...rest };
    };

    switch (data.action) {
        case CUSTOM_MENU_ACTION_TYPE.ADD_MENU:
            delete parsedData.id;
            if (parsedData.landingInfo?.attributes)
                parsedData.landingInfo.attributes = parseAttributes(parsedData.landingInfo.attributes);
            return parsedData;
        case CUSTOM_MENU_ACTION_TYPE.UPDATE_MENU:
            if (parsedData.landingInfo?.attributes)
                parsedData.landingInfo.attributes = parseAttributes(parsedData.landingInfo.attributes);
            return parsedData;
        default:
            return parsedData;
    }
};

const updateCustomMenusChangeOrder = (stage, project, newMenus, setId) => {
    return new Promise(async (resolve, reject) => {
        try {
            const originCustomMenus = await getCustomMenuSetDetailAPI(stage.endpoint, project.id, setId);
            const originCustomMenusIds = originCustomMenus.data?.menus?.reduce((acc, cur) => {
                acc.push(cur.id);
                if (cur.children) {
                    const newSubMenus = cur.children.reduce((_acc, _cur) => {
                        _acc.push(_cur.id);
                        return _acc;
                    }, []);
                    acc = acc.concat(newSubMenus);
                }
                return acc;
            }, []);

            const newCustomMenuIds = newMenus?.reduce((acc, cur) => {
                acc.push(cur.id);
                if (cur.children) {
                    const newSubMenus = cur.children.reduce((_acc, _cur) => {
                        _acc.push(_cur.id);
                        return _acc;
                    }, []);
                    acc = acc.concat(newSubMenus);
                }
                return acc;
            }, []);

            const isOrderChanged = originCustomMenusIds.some((id, index) => id !== newCustomMenuIds[index]);

            if (isOrderChanged) {
                await updateCustomMenuActionsAPI(stage.endpoint, project.id, setId, {
                    type: "UPDATE_MENU_ORDER",
                    data: { orderedIds: newCustomMenuIds },
                });
            }
            resolve(newCustomMenuIds);
        } catch (e) {
            console.log(e);
            reject(e);
        }
    });
};

export const updateCustomMenu =
    ({ setId, data }) =>
    (dispatch, getState) => {
        const { stage, project } = getState();

        return new Promise(async (resolve, reject) => {
            try {
                let _setId;
                if (!setId) {
                    const response = await dispatch(createCustomMenuSet({ data: { name: "Custom Menu Set" } }));
                    _setId = response.data?.id;
                } else {
                    _setId = setId;
                }

                const resetMenuUpdateAction = (menu) => {
                    const { customMenus } = getState();
                    let _data = customMenus?.menuSetSettings?.menus?.map((m) => {
                        if (m.id === menu.id) {
                            delete menu.action;
                            return menu;
                        } else {
                            if (m.children) {
                                return {
                                    ...m,
                                    children: m.children.map((subMenu) => {
                                        if (subMenu.id === menu.id) {
                                            delete menu.action;
                                            return menu;
                                        } else return subMenu;
                                    }),
                                };
                            }
                        }
                        return m;
                    });
                    dispatch({ type: UPDATE_MENU_SET_SETTINGS_STATE, payload: _data });
                };

                const resetMenuDeleteAction = (id) => {
                    const { customMenus } = getState();
                    const _data = customMenus?.menuSetSettings?.menus
                        ?.filter((menu) => menu.id !== id)
                        .map((menu) => {
                            if (menu.children) {
                                return {
                                    ...menu,
                                    children: menu.children.filter((subMenu) => subMenu.id !== id),
                                };
                            } else return menu;
                        });
                    dispatch({ type: UPDATE_MENU_SET_SETTINGS_STATE, payload: _data });
                };

                // Delete Menus
                const menusDelete = data.reduce((acc, cur) => {
                    if (cur.action === CUSTOM_MENU_ACTION_TYPE.DELETE_MENU) {
                        acc.push(cur.id);
                    }
                    if (cur.children) {
                        const newSubMenus = cur.children.reduce((_acc, _cur) => {
                            if (_cur.action === CUSTOM_MENU_ACTION_TYPE.DELETE_MENU) {
                                _acc.push(_cur.id);
                            }
                            return _acc;
                        }, []);
                        acc = acc.concat(newSubMenus);
                    }
                    return acc;
                }, []);

                // Add Menus
                const newMenus = await Promise.all(
                    data.map(async (menu) => {
                        let _menu = { ...JSON.parse(JSON.stringify(menu)) };
                        if (menu.action === CUSTOM_MENU_ACTION_TYPE.ADD_MENU) {
                            _menu = parseMenuData(_menu);
                            const { children, ...restMenuObj } = _menu;
                            const response = await addCustomMenuAPI(stage.endpoint, project.id, _setId, {
                                ...restMenuObj,
                            });
                            if (response.data) {
                                delete _menu.action;
                                _menu.id = response.data.id;
                                _menu.landingInfo = menu.landingInfo;
                            }
                        }

                        if (_menu.children) {
                            const newChildren = await Promise.all(
                                _menu.children.map(async (subMenu) => {
                                    if (subMenu.action === CUSTOM_MENU_ACTION_TYPE.ADD_MENU) {
                                        const subData = parseMenuData({
                                            ...JSON.parse(JSON.stringify(subMenu)),
                                            parentId: _menu.id,
                                        });
                                        const response = await addCustomMenuAPI(
                                            stage.endpoint,
                                            project.id,
                                            _setId,
                                            subData,
                                        );
                                        const subMenuId = response.data?.id;
                                        delete subData.action;
                                        return {
                                            id: subMenuId,
                                            ...subData,
                                            landingInfo: { ...subMenu.landingInfo },
                                        };
                                    } else return subMenu;
                                }),
                            );
                            return { ..._menu, children: newChildren };
                        } else return { ..._menu };
                    }),
                );

                dispatch({ type: UPDATE_MENU_SET_SETTINGS_STATE, payload: newMenus });

                // Update Menus
                const menusUpdate = newMenus.reduce((acc, cur) => {
                    if (cur.action === CUSTOM_MENU_ACTION_TYPE.UPDATE_MENU) {
                        acc.push(cur);
                    }
                    if (cur.children) {
                        const newSubMenus = cur.children.reduce((_acc, _cur) => {
                            if (_cur.action === CUSTOM_MENU_ACTION_TYPE.UPDATE_MENU) {
                                _acc.push({ parentId: cur.id, ..._cur });
                            }
                            return _acc;
                        }, []);
                        acc = acc.concat(newSubMenus);
                    }
                    return acc;
                }, []);

                const results = await Promise.allSettled([
                    ...menusDelete.map(async (menuId) => {
                        await deleteCustomMenuAPI(stage.endpoint, project.id, _setId, menuId);
                        await resetMenuDeleteAction(menuId);
                    }),
                    ...menusUpdate.map(async (menu) => {
                        const _menu = { ...JSON.parse(JSON.stringify(menu)) };
                        const { id, ...data } = parseMenuData(_menu);
                        await updateCustomMenuAPI(stage.endpoint, project.id, _setId, id, data);
                        await resetMenuUpdateAction(menu);
                    }),
                ]);

                // Change Order
                const { customMenus } = getState();
                const menus = customMenus?.menuSetSettings?.menus;
                await updateCustomMenusChangeOrder(stage, project, menus, _setId);
                resolve(results);
            } catch (error) {
                reject(error);
            }
        });
    };

const fetchByIds = async (apiCallback, ids) => {
    try {
        const params = { ids: ids.join(",") };
        const response = await apiCallback(params);
        return response.data;
    } catch (error) {
        console.error(`API 호출 중 오류가 발생했습니다:`, error);
        return null;
    }
};

const fetchAllResourceDetails = async (stage, project, resourceIds) => {
    const { virtualFolders, categories, collections, customMetadata } = resourceIds;
    const folderDetails = [];
    const categoryDetails = [];
    const collectionDetails = [];
    const customMetadataDetails = [];

    // 폴더 정보 조회
    if (Object.keys(virtualFolders).length > 0) {
        for (const driveId of Object.keys(virtualFolders)) {
            if (virtualFolders[driveId].length > 0) {
                const folderApiCallback = (params) =>
                    getFoldersListAPI(stage.endpoint, {
                        driveId,
                        queryParameters: params,
                        headerParams: {
                            projectId: project.id,
                            stageId: stage.id,
                        },
                    });

                const result = await fetchByIds(folderApiCallback, virtualFolders[driveId]);

                if (result.results?.length > 0) {
                    folderDetails.push(...result.results);
                }
            }
        }
    }

    if (Object.keys(categories).length > 0) {
        for (const categoryGroupId of Object.keys(categories)) {
            if (categories[categoryGroupId].length > 0) {
                const categoryApiCallback = (params) =>
                    getCategoryGroupCategoriesAPI(stage.id, stage.endpoint, project.id, categoryGroupId, params);

                const result = await fetchByIds(categoryApiCallback, categories[categoryGroupId]);

                if (result.categories?.length > 0) {
                    categoryDetails.push(...result.categories);
                }
            }
        }
    }

    if (collections.ids.length > 0) {
        const collectionApiCallback = (params) => getCollectionsAPI(stage.endpoint, project.id, params);

        const result = await fetchByIds(collectionApiCallback, collections.ids);

        if (result.results?.length > 0) {
            collectionDetails.push(...result.results);
        }
    }

    if (customMetadata.ids.length > 0) {
        const customMetadataApiCallback = (params) =>
            getContentSchemasAPI(stage.id, stage.endpoint, project.id, params);

        const result = await fetchByIds(customMetadataApiCallback, customMetadata.ids);

        if (result.results?.length > 0) {
            customMetadataDetails.push(...result.results);
        }
    }

    return { folderDetails, categoryDetails, collectionDetails, customMetadataDetails };
};

const collectResourceIdsFromMenuAttributes = (data) => {
    const virtualFolders = {};
    const categories = {};
    const collections = { ids: [] };

    const customMetadata = { ids: [] };

    const collectItemResourceIds = (item) => {
        const attributes = item.landingInfo?.attributes;

        if (attributes?.virtualFolder) {
            const driveId = attributes.virtualFolder.driveId;
            if (!virtualFolders[driveId]) {
                virtualFolders[driveId] = [];
            }
            virtualFolders[driveId].push(attributes.virtualFolder.virtualFolderId);
        }

        if (attributes?.categories) {
            const categoryGroupId = attributes.categories.categoryGroupId;
            if (!categories[categoryGroupId]) {
                categories[categoryGroupId] = [];
            }

            if (Array.isArray(attributes.categories.categories)) {
                attributes.categories.categories.forEach((category) => {
                    if (category.id) {
                        categories[categoryGroupId].push(category.id);
                    }
                });
            }
        }
        if (attributes?.collections) {
            attributes.collections.forEach((collection) => {
                if (collection.id) {
                    collections.ids.push(collection.id);
                }
            });
        }

        if (attributes?.customMetadata) {
            customMetadata.ids.push(attributes.customMetadata.id);
        }

        if (Array.isArray(item.children)) {
            item.children.forEach((child) => {
                collectItemResourceIds(child);
            });
        }
    };

    data.forEach((item) => collectItemResourceIds(item));

    Object.keys(virtualFolders).forEach((key) => {
        virtualFolders[key] = [...new Set(virtualFolders[key])];
    });

    Object.keys(categories).forEach((key) => {
        categories[key] = [...new Set(categories[key])];
    });

    collections.ids = [...new Set(collections.ids)];

    return { virtualFolders, categories, collections, customMetadata };
};

const updateMenuItemsWithDetails = (items, details) => {
    if (!items || !Array.isArray(items)) return;
    const { folderDetails, categoryDetails, collectionDetails, customMetadataDetails } = details;

    items.forEach((item) => {
        const attributes = item.landingInfo?.attributes;

        if (attributes?.virtualFolder) {
            const folder = folderDetails.find((f) => f.id.toString() === attributes?.virtualFolder?.virtualFolderId);
            if (folder) {
                attributes.virtualFolder.virtualFolderName = folder.name;
            }
        }

        if (attributes?.categories?.categories) {
            attributes.categories.categories?.forEach((category) => {
                if (category.id) {
                    const categoryInfo = categoryDetails.find((c) => c.id.toString() === category?.id);
                    if (categoryInfo) {
                        category.name = categoryInfo.name;
                    }
                }
            });
        }

        if (attributes?.collections) {
            attributes.collections?.forEach((collection) => {
                const collectionInfo = collectionDetails.find((c) => c.id.toString() === collection?.id);
                if (collectionInfo) {
                    collection.name = collectionInfo.name;
                }
            });
        }

        if (attributes?.customMetadata) {
            const customMetadataInfo = customMetadataDetails.find(
                (c) => c.id.toString() === attributes?.customMetadata?.id,
            );
            if (customMetadataInfo) {
                attributes.customMetadata.name = customMetadataInfo.name;
            }
        }

        if (item.children && Array.isArray(item.children)) {
            updateMenuItemsWithDetails(item.children, details);
        }
    });
};

export const getCustomMenuSetDetail = () => (dispatch, getState) => {
    const { stage, project } = getState();
    return new Promise(async (resolve, reject) => {
        try {
            dispatch({
                type: GET_CUSTOM_MENU_SET_DETAIL_PENDING,
            });

            const response = await getCustomMenuSetsAPI(stage.endpoint, project.id);
            const setId = response.data?.results?.[0]?.id;
            let payload = { menus: [] };

            if (setId) {
                const menuSetResponse = await getCustomMenuSetDetailAPI(stage.endpoint, project.id, setId);
                const resourceIds = collectResourceIdsFromMenuAttributes(menuSetResponse.data.menus);
                const details = await fetchAllResourceDetails(stage, project, resourceIds);
                updateMenuItemsWithDetails(menuSetResponse.data.menus, details);
                payload = menuSetResponse.data;
            }

            dispatch({
                type: GET_CUSTOM_MENU_SET_DETAIL_SUCCESS,
                payload,
            });
            resolve(payload);
        } catch (error) {
            dispatch({
                type: GET_CUSTOM_MENU_SET_DETAIL_FAILURE,
                payload: error,
            });
            reject(error);
        }
    });
};

export const loadCustomMenus =
    ({ isLibrary }) =>
    (dispatch, getState) => {
        const getComponentByDomain = (domain, type) => {
            // Lazy import to avoid circular dependency
            switch (domain) {
                case CUSTOM_MENU_DOMAIN.DASHBOARD:
                    return require("@routes/dashboard").default;
                case CUSTOM_MENU_DOMAIN.STORAGES:
                    return require("@routes/sources/List").default;
                case CUSTOM_MENU_DOMAIN.ASSETS:
                    if (type === CUSTOM_MENU_TYPE.LIST) return require("@routes/asset").default;
                    break;
                case CUSTOM_MENU_DOMAIN.COLLECTIONS:
                    if (type === CUSTOM_MENU_TYPE.LIST) return require("@routes/collections/list-collections").default;
                    return require("@routes/collections/detail-collection").default;
                default:
                    return null;
            }
        };
        const getFilteredMenus = (menus) => {
            if (isLibrary) return menus.filter((el) => el.libraryVisible);
            else return menus.filter((el) => el.consoleVisible);
        };

        const getConnectedMenuId = (domain) => {
            switch (domain) {
                case CUSTOM_MENU_DOMAIN.ASSETS:
                    return MENU_LIST.ASSETS;
                case CUSTOM_MENU_DOMAIN.COLLECTIONS:
                    return MENU_LIST.COLLECTIONS;
                default:
                    return null;
            }
        };
        return new Promise(async (resolve, reject) => {
            try {
                dispatch({
                    type: GET_CUSTOM_MENUS_PENDING,
                });
                const { stage, project } = getState();

                const customMenuSets = await getCustomMenuSetsAPI(stage.endpoint, project.id);
                const setId = customMenuSets?.data?.results?.[0]?.id;

                if (!setId) throw new Error("No Custom Menu Set Found");

                const response = await getCustomMenuSetDetailAPI(stage.endpoint, project.id, setId);

                const hasCustomContent = response.data?.menus?.some(
                    (m) =>
                        m.landingInfo?.domain === CUSTOM_MENU_DOMAIN.CUSTOM_CONTENTS ||
                        m.subMenus?.some((c) => c.landingInfo?.domain === CUSTOM_MENU_DOMAIN.CUSTOM_CONTENTS),
                );

                const data = getFilteredMenus(response.data?.menus)?.map((m) => {
                    const { id, name, iconType, children, landingInfo } = m;
                    return {
                        id,
                        name,
                        iconType,
                        isOpen: true,
                        subMenus: getFilteredMenus(children).map((c) => {
                            const { domain, type, attributes } = c.landingInfo ?? {};
                            return {
                                id: c.id,
                                name: c.name,
                                iconType: c.iconType,
                                searchParams: attributes,
                                component: getComponentByDomain(domain, type),
                                connectedMenuId: getConnectedMenuId(domain),
                                domain,
                                type,
                            };
                        }),
                        ...(landingInfo
                            ? {
                                  searchParams: landingInfo?.attributes,
                                  component: getComponentByDomain(landingInfo?.domain, landingInfo?.type),
                                  connectedMenuId: getConnectedMenuId(landingInfo?.domain),
                                  domain: landingInfo?.domain,
                                  type: landingInfo?.type,
                              }
                            : {}),
                    };
                });

                dispatch({
                    type: GET_CUSTOM_MENUS_SUCCESS,
                    payload: { data, setId, hasCustomContent, projectId: project.id },
                });
                resolve(data);
            } catch (error) {
                console.log(error);
                dispatch({ type: GET_CUSTOM_MENUS_FAILURE, payload: error });
                reject(error);
            }
        });
    };
